import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { OrganizationDto, OrganizationService } from '../services/organization.service';
import { HelcimAddressRequest, HelcimCustomerResponse, TestPaymentService } from '../services/test-payment.service';
import { PortalNavbarComponent } from './portal-navbar.component';

interface QuickLink {
  label: string;
  route: string;
  icon: string;
}

@Component({
  selector: 'app-payment-services-customer-get-customer-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <div class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/payment-services" class="home-btn">Payment Services</a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--emerald">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"></rect>
                <line x1="2" y1="10" x2="22" y2="10"></line>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Payment Services > Customer > Get Customer</h1>
          <p class="page-sub">View and edit customer details for the selected organization.</p>
        </div>
      </div>

      <section class="page-body">
        <div class="page-body-inner">
          <div class="related-card related-card--top">
            <h3>Quick Access</h3>
            <div class="related-grid">
              @for (link of relatedLinks; track link.route) {
                <a class="quick-card" [routerLink]="link.route">
                  <div class="quick-card-hd">
                    <span class="quick-icon">{{ link.icon }}</span>
                    <span>{{ link.label }}</span>
                  </div>
                </a>
              }
            </div>
          </div>

          <div class="card">
            <div class="card-hd">
              <span class="card-title">Customer Details</span>
            </div>

            <div class="form">
              <label class="field">
                <span>Organization</span>
                <select [(ngModel)]="organizationId" name="organizationId" [disabled]="isLoadingOrganizations || isLoading" (ngModelChange)="onOrganizationChanged()" required>
                  <option [ngValue]="0">{{ isLoadingOrganizations ? 'Loading organizations...' : 'Select organization' }}</option>
                  @for (organization of organizations; track organization.id) {
                    <option [ngValue]="organization.id">{{ organization.displayName }} ({{ organization.legalName }})</option>
                  }
                </select>
              </label>

              <label class="field">
                <span>Customer Id</span>
                <input type="text" [(ngModel)]="customerId" name="customerId" maxlength="120" />
              </label>

              <div class="actions actions--query">
                <button type="button" class="btn btn--primary" [disabled]="isLoading || organizationId <= 0 || !customerId.trim()" (click)="loadCustomer()">
                  {{ isLoading ? 'Loading...' : 'Get Customer' }}
                </button>
              </div>

              <label class="field"><span>Customer Code</span><input type="text" [(ngModel)]="form.customerCode" name="customerCode" maxlength="64" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Contact Name</span><input type="text" [(ngModel)]="form.contactName" name="contactName" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Business Name</span><input type="text" [(ngModel)]="form.businessName" name="businessName" maxlength="140" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Cell Phone</span><input type="text" [(ngModel)]="form.cellPhone" name="cellPhone" maxlength="32" (ngModelChange)="onModelChange()" /></label>

              <div class="section-title">Billing Address</div>
              <label class="field"><span>Name</span><input type="text" [(ngModel)]="form.billingAddress.name" name="billingName" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 1</span><input type="text" [(ngModel)]="form.billingAddress.street1" name="billingStreet1" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 2</span><input type="text" [(ngModel)]="form.billingAddress.street2" name="billingStreet2" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>City</span><input type="text" [(ngModel)]="form.billingAddress.city" name="billingCity" maxlength="80" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Province</span><input type="text" [(ngModel)]="form.billingAddress.province" name="billingProvince" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Country</span><input type="text" [(ngModel)]="form.billingAddress.country" name="billingCountry" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Postal Code</span><input type="text" [(ngModel)]="form.billingAddress.postalCode" name="billingPostalCode" maxlength="20" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Phone</span><input type="text" [(ngModel)]="form.billingAddress.phone" name="billingPhone" maxlength="32" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Email</span><input type="email" [(ngModel)]="form.billingAddress.email" name="billingEmail" maxlength="120" (ngModelChange)="onModelChange()" /></label>

              <div class="section-title">Shipping Address</div>
              <label class="field"><span>Name</span><input type="text" [(ngModel)]="form.shippingAddress.name" name="shippingName" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 1</span><input type="text" [(ngModel)]="form.shippingAddress.street1" name="shippingStreet1" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Street 2</span><input type="text" [(ngModel)]="form.shippingAddress.street2" name="shippingStreet2" maxlength="120" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>City</span><input type="text" [(ngModel)]="form.shippingAddress.city" name="shippingCity" maxlength="80" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Province</span><input type="text" [(ngModel)]="form.shippingAddress.province" name="shippingProvince" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Country</span><input type="text" [(ngModel)]="form.shippingAddress.country" name="shippingCountry" maxlength="40" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Postal Code</span><input type="text" [(ngModel)]="form.shippingAddress.postalCode" name="shippingPostalCode" maxlength="20" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Phone</span><input type="text" [(ngModel)]="form.shippingAddress.phone" name="shippingPhone" maxlength="32" (ngModelChange)="onModelChange()" /></label>
              <label class="field"><span>Email</span><input type="email" [(ngModel)]="form.shippingAddress.email" name="shippingEmail" maxlength="120" (ngModelChange)="onModelChange()" /></label>

              <div class="actions actions--save">
                <button type="button" class="btn btn--primary" [disabled]="true">
                  Save Customer (Moved to Organizations)
                </button>
              </div>
            </div>

            @if (errorMessage) {
              <div class="result result--error">{{ errorMessage }}</div>
            }

            @if (successMessage) {
              <div class="result result--ok">{{ successMessage }}</div>
            }
          </div>

        </div>
      </section>
    </div>
  `,
  styles: [`.page{min-height:100vh}.page-hero{border-bottom:1px solid var(--color-border);padding:2rem 1.5rem;background:linear-gradient(155deg,var(--color-bg-start) 0%,var(--color-bg-end) 100%)}.page-hero-inner,.page-body-inner{max-width:1080px;margin:0 auto}.home-btn{color:var(--color-muted);text-decoration:none}.hero-top-row{align-items:center;display:flex;flex-wrap:wrap;gap:.85rem;margin-top:.75rem}.hero-icon{align-items:center;border-radius:0.9rem;display:flex;height:56px;justify-content:center;width:56px}.hero-icon--emerald{background:color-mix(in srgb,#10b981 18%,transparent);border:1px solid color-mix(in srgb,#10b981 35%,transparent);color:#34d399}.page-title{margin:.8rem 0 0}.page-sub{margin:.4rem 0 0;color:var(--color-muted)}.page-body{padding:1.5rem}.card,.related-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent)}.card{padding-bottom:1rem}.related-card{padding:1rem 1.1rem}.related-card--top{margin-bottom:1rem}.related-card h3{margin:0 0 .55rem}.related-grid{display:grid;gap:.8rem;grid-template-columns:repeat(auto-fit,minmax(220px,1fr))}.quick-card{border:1px solid var(--color-border);border-radius:.9rem;background:color-mix(in srgb,var(--color-panel) 80%,transparent);padding:.85rem 1rem;text-decoration:none;color:var(--color-text);transition:transform .15s ease,border-color .15s ease}.quick-card:hover{transform:translateY(-2px);border-color:color-mix(in srgb,var(--color-accent) 38%,var(--color-border))}.quick-card-hd{display:flex;align-items:center;gap:.5rem}.quick-icon{display:inline-flex;align-items:center;justify-content:center;width:1.2rem;height:1.2rem}.card-hd{border-bottom:1px solid var(--color-border);padding:.8rem 1rem}.card-title{font-weight:600}.form{display:grid;grid-template-columns:1fr 1fr;gap:.8rem;padding:1rem}.section-title{grid-column:1 / -1;font-weight:600;margin-top:.35rem}.field{display:flex;flex-direction:column;gap:.35rem}.field input,.field select{border:1px solid var(--color-border);border-radius:.45rem;background:transparent;color:var(--color-text);padding:.45rem .55rem}.field select{color-scheme:dark}.field select option{background:var(--color-bg-end);color:var(--color-text)}.actions{grid-column:1 / -1;display:flex;justify-content:flex-start}.actions--query{padding-top:.25rem}.actions--save{padding-top:.25rem}.btn{align-items:center;border:1px solid transparent;border-radius:.6rem;cursor:pointer;display:inline-flex;font-size:.78rem;font-weight:600;gap:.35rem;line-height:1;padding:.6rem .9rem;transition:all .18s ease}.btn:disabled{cursor:not-allowed;opacity:.55}.btn--primary{background:linear-gradient(135deg,#2563eb,#1d4ed8);border-color:#1d4ed8;color:#fff}.result{margin:0 1rem .9rem;padding:.75rem;border-radius:.55rem;font-size:.9rem;display:grid;gap:.25rem}.result--ok{border:1px solid color-mix(in srgb,#10b981 40%,transparent);background:color-mix(in srgb,#10b981 10%,transparent)}.result--error{border:1px solid color-mix(in srgb,#ef4444 40%,transparent);background:color-mix(in srgb,#ef4444 10%,transparent)}`]
})
export class PaymentServicesCustomerGetCustomerPageComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly testPaymentService = inject(TestPaymentService);
  private readonly route = inject(ActivatedRoute);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  organizationId = 0;
  customerId = '';
  organizations: OrganizationDto[] = [];
  isLoadingOrganizations = false;

  isLoading = false;
  isSaving = false;
  isDirty = false;
  errorMessage = '';
  successMessage = '';
  selectedOrganization: OrganizationDto | null = null;

  private originalModel: string | null = null;

  form = {
    customerCode: '',
    contactName: '',
    businessName: '',
    cellPhone: '',
    billingAddress: createAddress(),
    shippingAddress: createAddress()
  };

  readonly relatedLinks: QuickLink[] = [
    { label: 'Get Customers', route: '/payment-services/customer/get-customers', icon: '👥' }
  ];

  async ngOnInit(): Promise<void> {
    await this.loadOrganizationsAsync();

    const queryOrganizationId = Number(this.route.snapshot.queryParamMap.get('organizationId') ?? '0');
    const queryCustomerId = this.route.snapshot.queryParamMap.get('customerId') ?? '';

    if (queryOrganizationId > 0) {
      this.organizationId = queryOrganizationId;
    }

    if (queryCustomerId.trim()) {
      this.customerId = queryCustomerId.trim();
    }

    if (this.organizationId > 0 && this.customerId) {
      await this.loadCustomer();
    }
  }

  onModelChange(): void {
    this.successMessage = '';
    this.isDirty = this.originalModel !== null && this.originalModel !== JSON.stringify(this.form);
  }

  private async loadOrganizationsAsync(): Promise<void> {
    this.isLoadingOrganizations = true;

    try {
      const isSiteAdmin = await this.authService.hasRoleAsync('SiteAdmin');
      this.organizations = isSiteAdmin
        ? await firstValueFrom(this.organizationService.getAll())
        : [await firstValueFrom(this.organizationService.getMyOrganization())];

      if (this.organizationId <= 0 && this.organizations.length === 1) {
        this.organizationId = this.organizations[0].id;
      }

      this.onOrganizationChanged();
    } catch {
      this.organizations = [];
      this.selectedOrganization = null;
      this.errorMessage = 'Unable to load organizations.';
    } finally {
      this.isLoadingOrganizations = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async loadCustomer(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.organizationId <= 0 || !this.customerId.trim()) {
      this.errorMessage = 'Organization and customer id are required.';
      return;
    }

    this.isLoading = true;

    try {
      const response = await this.testPaymentService.getCustomer(this.organizationId, this.customerId.trim()).toPromise();

      if (!response) {
        throw new Error('Get customer request returned no data.');
      }

      this.applyResponse(response);
      this.applyOrganizationBusinessNamesToAddresses();
      this.originalModel = JSON.stringify(this.form);
      this.isDirty = false;
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Get customer request failed.';
    } finally {
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  async saveCustomer(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (this.organizationId <= 0 || !this.customerId.trim()) {
      this.errorMessage = 'Organization and customer id are required.';
      return;
    }

    if (!this.isDirty) {
      return;
    }

    this.isSaving = true;

    try {
      const mappedContactName = this.selectedOrganization?.contactName?.trim() || this.form.contactName.trim() || undefined;
      const mappedBusinessName = this.selectedOrganization?.legalName?.trim() || this.form.businessName.trim() || undefined;
      const mappedCellPhone = this.selectedOrganization?.billingContactPhone?.trim() || this.form.cellPhone.trim() || undefined;

      this.applyOrganizationBusinessNamesToAddresses();
      this.applyOrganizationPhonesToAddresses();

      const response = await this.testPaymentService.updateCustomer(this.customerId.trim(), {
        organizationId: this.organizationId,
        customerId: this.customerId.trim(),
        customerCode: this.form.customerCode.trim() || undefined,
        contactName: mappedContactName,
        businessName: mappedBusinessName,
        cellPhone: mappedCellPhone,
        billingAddress: toRequestAddress(this.form.billingAddress),
        shippingAddress: toRequestAddress(this.form.shippingAddress)
      }).toPromise();

      if (!response) {
        throw new Error('Update customer request returned no data.');
      }

      this.applyResponse(response);
      this.originalModel = JSON.stringify(this.form);
      this.isDirty = false;
      this.successMessage = 'Customer saved successfully.';
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Update customer request failed.';
    } finally {
      this.isSaving = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private applyResponse(response: HelcimCustomerResponse): void {
    this.customerId = response.customerId || this.customerId;
    this.form.customerCode = response.customerCode ?? '';
    this.form.contactName = response.contactName ?? '';
    this.form.businessName = response.businessName ?? '';
    this.form.cellPhone = response.cellPhone ?? '';
    this.form.billingAddress = fromResponseAddress(response.billingAddress);
    this.form.shippingAddress = fromResponseAddress(response.shippingAddress);
  }

  private syncSelectedOrganization(): void {
    this.selectedOrganization = this.organizations.find((organization) => organization.id === this.organizationId) ?? null;
  }

  onOrganizationChanged(): void {
    this.syncSelectedOrganization();
    const rawCustomerId = this.selectedOrganization?.helcimCustomerId;
    this.customerId = rawCustomerId === undefined || rawCustomerId === null
      ? ''
      : String(rawCustomerId).trim();
  }

  private applyOrganizationBusinessNamesToAddresses(): void {
    this.syncSelectedOrganization();

    const billingBusinessName = this.selectedOrganization?.billingBusinessName?.trim();
    if (billingBusinessName) {
      this.form.billingAddress.name = billingBusinessName;
    }

    const shippingBusinessName = this.selectedOrganization?.shippingBusinessName?.trim();
    if (shippingBusinessName) {
      this.form.shippingAddress.name = shippingBusinessName;
    }
  }

  private applyOrganizationPhonesToAddresses(): void {
    this.syncSelectedOrganization();

    const billingPhone = this.selectedOrganization?.billingContactPhone?.trim();
    if (billingPhone) {
      this.form.billingAddress.phone = billingPhone;
      this.form.cellPhone = billingPhone;
    }

    const shippingPhone = this.selectedOrganization?.shippingContactPhone?.trim();
    if (shippingPhone) {
      this.form.shippingAddress.phone = shippingPhone;
    }
  }
}

function createAddress() {
  return {
    name: '',
    street1: '',
    street2: '',
    city: '',
    province: '',
    country: '',
    postalCode: '',
    phone: '',
    email: ''
  };
}

function fromResponseAddress(address: {
  name?: string;
  street1?: string;
  street2?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  phone?: string;
  email?: string;
} | undefined) {
  return {
    name: address?.name ?? '',
    street1: address?.street1 ?? '',
    street2: address?.street2 ?? '',
    city: address?.city ?? '',
    province: address?.province ?? '',
    country: address?.country ?? '',
    postalCode: address?.postalCode ?? '',
    phone: address?.phone ?? '',
    email: address?.email ?? ''
  };
}

function toRequestAddress(address: {
  name: string;
  street1: string;
  street2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  phone: string;
  email: string;
}): HelcimAddressRequest {
  return {
    name: address.name.trim(),
    street1: address.street1.trim(),
    street2: address.street2.trim() || undefined,
    city: address.city.trim(),
    province: address.province.trim() || undefined,
    country: address.country.trim() || undefined,
    postalCode: address.postalCode.trim(),
    phone: address.phone.trim() || undefined,
    email: address.email.trim() || undefined
  };
}
